// RUTA: src/components/fitness/BookTrainingCard.tsx
// Botón de "Reservar entrenamiento" que vive en el navbar y abre un
// modal con el flujo completo de reserva. Antes era un card grande en
// el home; lo movimos al header para no robarle espacio al dashboard
// y porque reservar es una acción puntual, no algo que mires todo el
// día.
//
// Lógica:
//   - Si el cliente NO tiene coaches asignados → no se renderiza nada
//     (ni siquiera el botón).
//   - Si tiene 1 coach → modal abre directo en el flujo de ese coach.
//     Sin horarios → CTA WhatsApp con mensaje prefillado.
//   - Si tiene 2+ coaches → modal abre selector de coach. Coaches
//     is_test_account=true llevan badge "prueba" para distinguirlos
//     del coach principal.

import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { CalendarPlus, Clock, CheckCircle2, AlertCircle, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { BookTrainingDialog } from './BookTrainingDialog';

interface UpcomingSession {
  id: string;
  starts_at: string;
  ends_at: string;
  status: string;
  coach_id: string;
  client_note?: string | null;
}

interface CoachOption {
  id: string;
  display_name: string;
  bookings_open: boolean;
  rules_count: number;
  whatsapp_number: string | null;
  is_test_account: boolean;
  notes_for_client: string | null;
}

interface Props {
  /** Estilo del trigger.
   *  - 'navbar' = ícono compacto del header.
   *  - 'menu'   = item con ícono+texto para menú móvil/hamburger.
   *  - 'promo'  = card grande dentro del home con CTA llamativo y
   *               animación. Cambia su contenido según haya o no
   *               sesiones próximas. */
  variant?: 'navbar' | 'menu' | 'promo';
  /** En modo 'menu' permite cerrar el menú padre tras click. */
  onTriggerClick?: () => void;
}

export function BookTrainingCard({ variant = 'navbar', onTriggerClick }: Props = {}) {
  const [coaches, setCoaches] = useState<CoachOption[]>([]);
  const [upcoming, setUpcoming] = useState<UpcomingSession[]>([]);
  const [clientName, setClientName] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [openCoachId, setOpenCoachId] = useState<string | null>(null);
  // Sesión seleccionada para ver detalle / cancelar.
  const [detailSession, setDetailSession] = useState<UpcomingSession | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: prof } = await supabase
        .from('profiles').select('display_name')
        .eq('id', user.id).maybeSingle();
      setClientName((prof as any)?.display_name || 'tu cliente');

      const { data: rels } = await supabase
        .from('coach_clients').select('coach_id')
        .eq('client_id', user.id);
      const candidateIds = (rels ?? [])
        .map(r => r.coach_id as string)
        .filter(id => id !== user.id);
      if (candidateIds.length === 0) { setCoaches([]); return; }

      const [{ data: profs }, { data: settings }, { data: rules }] = await Promise.all([
        supabase.from('profiles').select('id, display_name').in('id', candidateIds),
        supabase.from('coach_booking_settings')
          .select('coach_id, bookings_open, notes_for_client, whatsapp_number, is_test_account')
          .in('coach_id', candidateIds),
        supabase.from('coach_availability_rules')
          .select('coach_id').in('coach_id', candidateIds).eq('is_active', true),
      ]);

      const profByCoach = new Map<string, string>();
      for (const p of profs ?? []) profByCoach.set((p as any).id, (p as any).display_name || 'Coach');

      const settingsByCoach = new Map<string, any>();
      for (const s of settings ?? []) settingsByCoach.set((s as any).coach_id, s);

      const rulesByCoach = new Map<string, number>();
      for (const r of rules ?? []) {
        const k = (r as any).coach_id;
        rulesByCoach.set(k, (rulesByCoach.get(k) ?? 0) + 1);
      }

      const opts: CoachOption[] = candidateIds.map(id => {
        const s = settingsByCoach.get(id);
        return {
          id,
          display_name: profByCoach.get(id) ?? 'Coach',
          bookings_open: !!s?.bookings_open,
          rules_count: rulesByCoach.get(id) ?? 0,
          whatsapp_number: s?.whatsapp_number ?? null,
          is_test_account: !!s?.is_test_account,
          notes_for_client: s?.notes_for_client ?? null,
        };
      });
      // Coaches PRINCIPALES primero (is_test_account=false) y los
      // de prueba al final. Cuando solo hay 1 coach esto no cambia
      // nada; cuando hay múltiples (fase de pruebas), el real
      // siempre aparece arriba del de prueba en el selector.
      opts.sort((a, b) => Number(a.is_test_account) - Number(b.is_test_account));
      setCoaches(opts);

      const { data: sessions } = await supabase
        .from('training_sessions')
        .select('id, starts_at, ends_at, status, coach_id, client_note')
        .eq('client_id', user.id)
        .in('coach_id', candidateIds)
        .in('status', ['requested', 'confirmed'])
        .gte('starts_at', new Date().toISOString())
        .order('starts_at', { ascending: true })
        .limit(5);
      setUpcoming((sessions as UpcomingSession[]) || []);
    })();
  }, [refreshTick]);

  const formatSession = useMemo(() => (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('es-MX', {
      weekday: 'short', day: 'numeric', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  }, []);

  // Sin coaches asignados → no renderizamos NADA (ni botón ni modal).
  if (coaches.length === 0) return null;

  /** Resuelve qué hacer al hacer click en un coach del selector. */
  const handleCoachClick = (coach: CoachOption) => {
    const hasAvailability = coach.bookings_open && coach.rules_count > 0;
    if (hasAvailability) {
      setOpenCoachId(coach.id);
      return;
    }
    if (coach.whatsapp_number) {
      const msg = encodeURIComponent(
        `Hola coach ${coach.display_name}, soy ${clientName}.\n\n` +
        `Quiero agendar un entrenamiento personal contigo desde la app, pero no veo horarios disponibles.\n\n` +
        `¿Puedes abrir espacios en tu calendario de la app, por favor?\n\n` +
        `¿Me confirmas cuando ya tengas horarios disponibles para que pueda agendar desde la app?\n\n` +
        `Gracias 💪`
      );
      const phone = coach.whatsapp_number.replace(/\D/g, '');
      window.location.href = `https://wa.me/${phone}?text=${msg}`;
      return;
    }
    alert(`${coach.display_name} aún no tiene horarios disponibles ni canal de contacto configurado.`);
  };

  const openModal = () => {
    setRefreshTick(t => t + 1); // refrescar data al abrir
    setModalOpen(true);
    onTriggerClick?.();
  };

  // ── Trigger button: 3 variantes según dónde se monte ───────────────
  const hasUpcoming = upcoming.length > 0;
  const nextSession = upcoming[0];

  let trigger: JSX.Element;
  if (variant === 'menu') {
    trigger = (
      <button
        onClick={openModal}
        className="w-full flex items-center gap-3 px-5 py-3 min-h-[44px] text-sm font-bold text-primary-foreground/70 hover:text-accent hover:bg-accent/5 transition-colors"
      >
        <CalendarPlus className="w-4 h-4" />
        Reservar entrenamiento
        {hasUpcoming && (
          <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-accent/20 text-accent">
            {upcoming.length}
          </span>
        )}
      </button>
    );
  } else if (variant === 'promo') {
    // Card grande dentro del home. Inteligente: si ya hay sesión
    // próxima, muestra info de esa sesión (modo informativo, sin
    // CTA agresivo). Si no, muestra invitación con animación glow.
    const nextLabel = nextSession ? formatSession(nextSession.starts_at) : '';
    const nextCoachName = nextSession
      ? (coaches.find(c => c.id === nextSession.coach_id)?.display_name ?? 'tu coach')
      : '';

    trigger = hasUpcoming ? (
      // ─── Caso 2: ya tiene sesión próxima ─────────────────────
      <button
        onClick={openModal}
        className="w-full flex items-center gap-4 p-4 md:p-5 rounded-2xl bg-background/60 dark:bg-white/5 backdrop-blur-sm border-2 border-accent/30 hover:border-accent/50 transition-all text-left group"
      >
        <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center flex-shrink-0">
          {nextSession.status === 'confirmed'
            ? <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />
            : <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-wider text-muted-foreground dark:text-primary-foreground/50 mb-0.5">
            {nextSession.status === 'confirmed' ? 'Sesión confirmada' : 'Sesión pendiente de confirmar'}
          </div>
          <div className="text-sm font-black truncate text-foreground dark:text-primary-foreground">{nextLabel}</div>
          <div className="text-xs text-muted-foreground dark:text-primary-foreground/60 truncate">con {nextCoachName}</div>
        </div>
        <span className="text-[11px] font-bold uppercase tracking-wider text-accent group-hover:translate-x-1 transition-transform whitespace-nowrap">
          Ver →
        </span>
      </button>
    ) : (
      // ─── Caso 1: sin sesión, CTA llamativo con animación ─────
      <button
        onClick={openModal}
        className="w-full flex items-center gap-4 p-4 md:p-5 rounded-2xl bg-gradient-to-br from-accent/15 via-accent/8 to-transparent dark:from-accent/20 dark:via-accent/10 dark:to-transparent border-2 border-accent/40 hover:border-accent/60 transition-all text-left group animate-glow-pulse"
      >
        <div className="w-12 h-12 rounded-xl bg-accent/25 flex items-center justify-center flex-shrink-0">
          <CalendarPlus className="w-6 h-6 text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm md:text-base font-black uppercase tracking-wider text-foreground dark:text-primary-foreground">
            ¿Listo para subir el nivel?
          </div>
          <div className="text-xs text-muted-foreground dark:text-primary-foreground/60 mt-0.5">
            Reserva una sesión 1-on-1 con tu coach.
          </div>
        </div>
        <span className="relative overflow-hidden inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-accent text-accent-foreground text-[11px] font-black uppercase tracking-wider shadow-md group-hover:shadow-accent/40 transition-shadow whitespace-nowrap">
          <span className="relative z-10 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            Reservar
          </span>
          {/* Shine effect on hover */}
          <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
        </span>
      </button>
    );
  } else {
    // variant === 'navbar' (default)
    trigger = (
      <button
        onClick={openModal}
        title="Reservar entrenamiento personalizado con tu coach"
        aria-label="Reservar entrenamiento"
        className="relative inline-flex items-center gap-1.5 h-10 min-h-[40px] px-2.5 sm:px-3 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 hover:border-accent/40 hover:bg-accent/10 transition-all duration-200 text-primary-foreground/70 hover:text-accent"
      >
        <CalendarPlus className="w-4 h-4 flex-shrink-0" />
        <span className="text-[11px] font-bold uppercase tracking-wider">Reservar</span>
        {hasUpcoming && (
          <span className="ml-0.5 min-w-[18px] h-[18px] rounded-full bg-accent text-accent-foreground text-[10px] font-black flex items-center justify-center px-1">
            {upcoming.length}
          </span>
        )}
      </button>
    );
  }

  return (
    <>
      {trigger}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-accent" />
              Entrenamiento personalizado
            </DialogTitle>
            <DialogDescription>
              {coaches.length === 1
                ? `Reserva una sesión con ${coaches[0].display_name}.`
                : 'Elige con qué coach quieres entrenar.'}
            </DialogDescription>
          </DialogHeader>

          {/* Próximas sesiones */}
          {upcoming.length > 0 && (
            <div className="space-y-1.5 py-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Tus próximas sesiones
              </div>
              {upcoming.map(s => {
                const coachName = coaches.find(c => c.id === s.coach_id)?.display_name || 'Coach';
                return (
                  <button
                    key={s.id}
                    onClick={() => setDetailSession(s)}
                    className="w-full flex items-center gap-2 text-xs bg-secondary/40 hover:bg-secondary/60 border border-border hover:border-accent/40 rounded-md px-2.5 py-1.5 transition-colors text-left"
                  >
                    {s.status === 'confirmed'
                      ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                      : <AlertCircle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />}
                    <span className="font-semibold flex-1 truncate">
                      {formatSession(s.starts_at)}
                      <span className="text-muted-foreground font-normal"> · {coachName}</span>
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${
                      s.status === 'confirmed' ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'
                    }`}>
                      {s.status === 'confirmed' ? 'Confirmada' : 'Pendiente'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Selector de coaches */}
          <div className="space-y-2 py-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              {coaches.length === 1 ? 'Tu coach' : 'Elige tu coach'}
            </div>
            {coaches.map(coach => {
              const hasAvailability = coach.bookings_open && coach.rules_count > 0;
              const statusLabel = hasAvailability
                ? '🟢 Horarios disponibles'
                : coach.whatsapp_number
                  ? '🟡 Sin horarios — pedirle por WhatsApp'
                  : '🟡 Sin horarios disponibles';
              const disabled = !hasAvailability && !coach.whatsapp_number;
              return (
                <button
                  key={coach.id}
                  onClick={() => handleCoachClick(coach)}
                  disabled={disabled}
                  className="w-full text-left bg-card hover:bg-card/80 disabled:opacity-50 disabled:cursor-not-allowed border-2 border-border hover:border-accent/40 rounded-xl p-3 transition-all flex items-center gap-3"
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    hasAvailability ? 'bg-accent/20 text-accent' : 'bg-muted text-muted-foreground'
                  }`}>
                    {hasAvailability ? <Clock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm">{coach.display_name}</span>
                      {coach.is_test_account && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-500/40">
                          prueba
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{statusLabel}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Diálogos de booking — uno por coach, abren cuando se elige */}
      {coaches.map(coach => (
        <BookTrainingDialog
          key={coach.id}
          open={openCoachId === coach.id}
          onOpenChange={(o) => !o && setOpenCoachId(null)}
          coachId={coach.id}
          coachNotes={coach.notes_for_client}
          onRequested={() => {
            setOpenCoachId(null);
            setModalOpen(false);
            setRefreshTick(t => t + 1);
          }}
        />
      ))}

      {/* Detalle de sesión + acción cancelar (si es pendiente) */}
      <Dialog open={!!detailSession} onOpenChange={(o) => !o && setDetailSession(null)}>
        <DialogContent className="max-w-md">
          {detailSession && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detailSession.status === 'confirmed'
                    ? <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                    : <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />}
                  Detalle de tu sesión
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-3 py-2">
                <DetailRow label="Fecha y hora" value={formatSession(detailSession.starts_at)} />
                <DetailRow
                  label="Coach"
                  value={coaches.find(c => c.id === detailSession.coach_id)?.display_name || 'Coach'}
                />
                <DetailRow
                  label="Estado"
                  value={
                    <span className={`font-black ${
                      detailSession.status === 'confirmed'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-amber-600 dark:text-amber-400'
                    }`}>
                      {detailSession.status === 'confirmed' ? '✅ Confirmada' : '🟡 Pendiente de confirmar'}
                    </span>
                  }
                />
                {detailSession.client_note && (
                  <DetailRow label="Tu nota" value={detailSession.client_note} multiline />
                )}
                {detailSession.status === 'confirmed' && (
                  <p className="text-[11px] text-muted-foreground bg-secondary/40 border border-border rounded-md p-2">
                    Para cancelar una sesión confirmada, contacta a tu coach directamente.
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button
                  onClick={() => setDetailSession(null)}
                  className="px-3 py-2 text-xs font-bold rounded-md hover:bg-secondary transition-colors min-h-[36px]"
                >
                  Cerrar
                </button>
                {detailSession.status === 'requested' && (
                  <button
                    onClick={async () => {
                      if (!confirm('¿Cancelar esta solicitud? Esta acción no se puede deshacer.')) return;
                      setCancelling(true);
                      const { error } = await supabase
                        .from('training_sessions')
                        .update({
                          status: 'cancelled',
                          cancelled_at: new Date().toISOString(),
                          cancelled_by: 'client',
                        })
                        .eq('id', detailSession.id);
                      setCancelling(false);
                      if (error) {
                        alert('No se pudo cancelar: ' + error.message);
                        return;
                      }
                      setDetailSession(null);
                      setRefreshTick(t => t + 1);
                    }}
                    disabled={cancelling}
                    className="px-3 py-2 text-xs font-black uppercase tracking-wider rounded-md bg-destructive text-destructive-foreground hover:opacity-90 transition-opacity min-h-[36px] disabled:opacity-50"
                  >
                    {cancelling ? 'Cancelando…' : 'Cancelar solicitud'}
                  </button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function DetailRow({ label, value, multiline }: { label: string; value: React.ReactNode; multiline?: boolean }) {
  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-0.5">{label}</div>
      <div className={`text-sm ${multiline ? 'whitespace-pre-line' : ''}`}>{value}</div>
    </div>
  );
}
